<template>
  <div class="border border-solid border-gray rounded-lg w-full cursor-pointer"
       :class="isSelected ? 'bubble-shadow border-focus' : 'focus:bubble-shadow focus:border-focus'"
       @click="selectBubble"
  >
    <div v-if="isTemplate"
         class="relative mt-1 mr-1"
    >
      <blocks-icon :is-filled="true" class="absolute top-0 right-0 test-template-icon h-4 w-4 pt-px pr-px"/>
    </div>

    <div class="border-0 border-b border-dashed border-gray mx-2 py-2">
      <vx-tooltip class="w-max text-sm cursor-not-allowed"
                  :text="headerTooltip"
                  position="bottom"
                  delay="300"
      >
        <span class="text-gray">From: </span>
        <span class="text-gray-dark">{{ email }}</span>
      </vx-tooltip>
    </div>

    <rich-input v-model="text"
                :key="actionId"
                :is-disabled="isTemplate"
                :autofocus="!text"
                :editor-class="contentDimClass"
                :toolbar-class="contentDimClass"
    >
      <template #prefix>
        <email-template-edit-overlay label="Edit Header"
                                     section="header"
                                     :active-section="activeOverlaySection"
                                     @activate="setActiveOverlaySection"
                                     @deactivate="clearActiveOverlaySection"
        >
          <div v-html="greeting"
               class="py-1 min-h-6"
          />
        </email-template-edit-overlay>
      </template>

      <template #suffix>
        <email-template-edit-overlay label="Edit Footer"
                                     section="footer"
                                     :active-section="activeOverlaySection"
                                     @activate="setActiveOverlaySection"
                                     @deactivate="clearActiveOverlaySection"
        >
          <div v-html="parting"
               class="py-1 min-h-6"
          />
        </email-template-edit-overlay>
      </template>
    </rich-input>
  </div>
</template>

<script>
import BoxWrapper from '@/components/base-components/BoxWrapper'
import RichInput from '@/components/base-components/rich-input/RichInput.vue'
import EmailTemplateEditOverlay from '@/components/preview/v2/EmailTemplateEditOverlay.vue'
import previewModule from '@/store/modules/preview.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import { STATE_TYPES } from '@/utils/bot-json.constants'
import { SECTION_FOCUSES } from '@/store/constants'
import conversationModule from '@/store/modules/conversation.module'
import contentUtils from '@/utils/content.utils'
import templateSelectorModule from '@/store/modules/template-selector.module'
import BlocksIcon from '@/assets/images/icons/BlocksIcon'
import isEmpty from 'ramda/src/isEmpty'
import variableRefsUtils from '@/utils/variable-refs.utils'

export default {
  name: 'EmailBubble',

  components: { RichInput, BoxWrapper, BlocksIcon, EmailTemplateEditOverlay },

  props: {
    stateId: {
      type: String,
      required: true
    },
    actionId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      greeting: '',
      parting: '',
      headerTooltip: 'Your customer will see the email address configured within your helpdesk software.',
      activeOverlaySection: null,
      STATE_TYPES,
      email: 'no-reply@your-company.com'
    }
  },

  watch: {
    'botEmailConfig.emailTemplate': {
      immediate: true,
      handler(emailTemplate) {
        if (!emailTemplate) {
          this.greeting = ''
          this.parting = ''
          return
        }

        const splitEmailTemplate = emailTemplate.split('{{content}}')
        this.greeting = variableRefsUtils.substituteVariableRefs(splitEmailTemplate[0].trim())
        this.parting = variableRefsUtils.substituteVariableRefs(splitEmailTemplate[1].trim())
      }
    }
  },

  computed: {
    botEmailConfig: previewModule.botEmailConfig,
    bubbleSelected: chatBubbleModule.bubbleSelected,

    state() {
      return conversationModule.getRootJson().states[this.stateId]
    },

    action() {
      return conversationModule.getAction(this.stateId, this.actionId)
    },

    isSelected() {
      return this.bubbleSelected.actionId === this.actionId && this.bubbleSelected.stateId === this.stateId
    },

    isTemplate() {
      return this.state.type === STATE_TYPES.TEMPLATE_REF
    },

    contentDimClass() {
      return this.activeOverlaySection ? 'email-template-section-dimmed' : null
    },

    text: {
      get() {
        return contentUtils.getText(this.action)
      },

      set(text) {
        const resolvedText = this.resolveText(text)
        const content = contentUtils.createContent(resolvedText, [], [])
        conversationModule.addOutputAction(this.actionId, content)
      }
    }
  },

  methods: {
    selectBubble() {
      chatBubbleModule.selectBotBubble(
        this.actionId,
        this.stateId,
        SECTION_FOCUSES.NONE,
        this.state.type === STATE_TYPES.TEMPLATE_REF
      )
      templateSelectorModule.hideSelector()
    },

    resolveText(text) {
      const textWithoutHtmlTags = contentUtils.stripDefaultHtmlTags(text)
      return isEmpty(textWithoutHtmlTags) ? '' : text
    },

    setActiveOverlaySection(section) {
      this.activeOverlaySection = section
    },

    clearActiveOverlaySection(section) {
      if (this.activeOverlaySection === section) {
        this.activeOverlaySection = null
      }
    }
  }
}
</script>

<style scoped lang="scss">
.bubble-shadow {
  // noinspection CssInvalidFunction - theme() belongs to TailWind
  box-shadow: 0 4px 11px 0 theme('colors.focus-a50');
}
</style>
